import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, Check, CheckCheck, Star, Briefcase, Users, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALERT_ICONS = {
  new_postings: Briefcase,
  hiring_surge: TrendingUp,
  new_department: Users,
  leadership_hire: Star,
};

const ALERT_COLORS = {
  new_postings: 'text-blue-600 bg-blue-50',
  hiring_surge: 'text-red-600 bg-red-50',
  new_department: 'text-purple-600 bg-purple-50',
  leadership_hire: 'text-amber-600 bg-amber-50',
};

const ALERT_LABELS = {
  new_postings: 'New Postings',
  hiring_surge: 'Hiring Surge',
  new_department: 'New Department',
  leadership_hire: 'Leadership Hire',
};

export const NotificationBell = () => {
  const [unread, setUnread] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const fetchCount = useCallback(async () => {
    const token = localStorage.getItem('backoffice_token');
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/intelligence/alerts/count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUnread(data.unread || 0);
      }
    } catch {}
  }, []);

  const fetchAlerts = useCallback(async () => {
    const token = localStorage.getItem('backoffice_token');
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/intelligence/alerts?limit=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setAlerts(await res.json());
      }
    } catch {}
    setLoading(false);
  }, []);

  const markAllRead = async () => {
    const token = localStorage.getItem('backoffice_token');
    if (!token) return;
    try {
      await fetch(`${API_URL}/api/intelligence/alerts/mark-all-read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlerts(prev => prev.map(a => ({ ...a, read: true })));
      setUnread(0);
    } catch {}
  };

  const markRead = async (alert) => {
    if (alert.read) return;
    const token = localStorage.getItem('backoffice_token');
    if (!token) return;
    try {
      await fetch(`${API_URL}/api/intelligence/alerts/mark-read/${encodeURIComponent(alert.company)}/${alert.alert_type}/${alert.date}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlerts(prev => prev.map(a =>
        a.company === alert.company && a.alert_type === alert.alert_type && a.date === alert.date
          ? { ...a, read: true } : a
      ));
      setUnread(prev => Math.max(0, prev - 1));
    } catch {}
  };

  useEffect(() => { fetchCount(); const i = setInterval(fetchCount, 60000); return () => clearInterval(i); }, [fetchCount]);

  useEffect(() => {
    if (open) fetchAlerts();
  }, [open, fetchAlerts]);

  useEffect(() => {
    const handler = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell-container">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-gray-400 hover:text-[#D4AF37] transition-colors rounded-lg hover:bg-white/5"
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1"
            data-testid="notification-badge">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50"
          data-testid="notification-panel">
          <div className="px-4 py-3 bg-[#0A1F44] flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[#D4AF37]">Watchlist Alerts</h3>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-[10px] text-gray-300 hover:text-[#D4AF37] flex items-center gap-1 transition-colors"
                data-testid="mark-all-read">
                <CheckCheck className="h-3 w-3" /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto" data-testid="notification-list">
            {loading ? (
              <div className="py-8 text-center text-sm text-gray-400">Loading...</div>
            ) : alerts.length === 0 ? (
              <div className="py-8 text-center" data-testid="no-alerts">
                <Bell className="h-8 w-8 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No alerts yet</p>
                <p className="text-xs text-gray-300 mt-1">Pin companies and alerts appear after the next data refresh</p>
              </div>
            ) : (
              alerts.map((alert, i) => {
                const Icon = ALERT_ICONS[alert.alert_type] || Bell;
                const colorCls = ALERT_COLORS[alert.alert_type] || 'text-gray-600 bg-gray-50';
                const label = ALERT_LABELS[alert.alert_type] || alert.alert_type;
                const detail = alert.data?.detail || '';

                return (
                  <div
                    key={`${alert.company}-${alert.alert_type}-${alert.date}-${i}`}
                    onClick={() => { markRead(alert); setOpen(false); navigate('/backoffice/intelligence/watchlist'); }}
                    className={`px-4 py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors flex items-start gap-3 ${!alert.read ? 'bg-blue-50/30' : ''}`}
                    data-testid={`alert-item-${i}`}
                  >
                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colorCls}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-[#0A1F44] truncate">{alert.company}</span>
                        {!alert.read && <span className="h-1.5 w-1.5 rounded-full bg-blue-500 flex-shrink-0" />}
                      </div>
                      <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold ${colorCls}`}>{label}</span>
                      <p className="text-[11px] text-gray-500 mt-0.5 truncate">{detail}</p>
                      <p className="text-[9px] text-gray-300 mt-1">{alert.date}</p>
                    </div>
                    {!alert.read && (
                      <button
                        onClick={(e) => { e.stopPropagation(); markRead(alert); }}
                        className="text-gray-300 hover:text-green-500 flex-shrink-0 mt-1"
                        title="Mark as read"
                      >
                        <Check className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {alerts.length > 0 && (
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-center">
              <button
                onClick={() => { setOpen(false); navigate('/backoffice/intelligence/watchlist'); }}
                className="text-xs text-[#D4AF37] hover:text-[#b89a2a] font-medium"
                data-testid="view-all-alerts"
              >
                View Watchlist Dashboard
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
